import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotateRight,
  faFileCsv,
  faSearch,
  faClose,
  faFingerprint,
  faEllipsisH,
  faBookOpen,
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { getAllBranches } from "../../../api/hrm/branches/info";
import { useDebounce } from "use-debounce";
import { hasPermission } from "../../../utils/permissionUtils";

const monthInputDefault = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
};

const AttendanceLogFilter = ({
  onNewClick,
  onFilterChange,
  onExportXlsxRequest,
  hasUnfinishedCheckin,
  attendanceStatus,
}) => {
  const [branchOptions, setBranchOptions] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [loadingManual, setLoadingManual] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMode, setExportMode] = useState("detail");
  const [exportBranchId, setExportBranchId] = useState("");
  const [exportMonth, setExportMonth] = useState(monthInputDefault());
  const [monthValue, setMonthValue] = useState(monthInputDefault());
  const menuRef = useRef();
  const isDisabled = hasUnfinishedCheckin;

  const [filters, setFilters] = useState({
    keyword: "",
    from_date: "",
    to_date: "",
  });

  // ============================================================
  // 🕓 Debounce keyword 0.5s
  // ============================================================
  const [debouncedKeyword] = useDebounce(filters.keyword, 500);

  useEffect(() => {
    if (!filters.from_date || !filters.to_date) return;
    onFilterChange?.({ ...filters, keyword: debouncedKeyword });
  }, [debouncedKeyword]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDateLocal = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const monthToDateRange = (monthStr) => {
    const safe = monthStr || monthInputDefault();
    const [yearStr, monthStrNum] = safe.split("-");
    const year = Number(yearStr);
    const month = Number(monthStrNum);
    const first = new Date(year, month - 1, 1);
    const last = new Date(year, month, 0);
    return {
      from_date: formatDateLocal(first),
      to_date: formatDateLocal(last),
    };
  };

  useEffect(() => {
    const initialMonth = monthInputDefault();
    const defaults = {
      keyword: "",
      ...monthToDateRange(initialMonth),
    };
    setMonthValue(initialMonth);
    setFilters(defaults);
    onFilterChange?.(defaults);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getAllBranches()
      .then((res) => {
        const branches = res.items || res;
        setBranchOptions(branches.map((b) => ({ value: b.id, label: b.name })));
      })
      .catch(console.error);
  }, []);

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") {
      onFilterChange?.(updated);
    }
  };

  const clearFilters = () => {
    const resetMonth = monthInputDefault();
    const cleared = {
      keyword: "",
      ...monthToDateRange(resetMonth),
    };
    setMonthValue(resetMonth);
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const handleOpenManual = async () => {
    try {
      setLoadingManual(true);
      const guideUrl = `${window.location.origin}/guides/hrm-attendance-guide.html`;
      window.open(guideUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      alert("Không thể tải hướng dẫn!");
    } finally {
      setLoadingManual(false);
      setShowMenu(false);
    }
  };

  const handleMonthChange = (nextMonth) => {
    setMonthValue(nextMonth);
    const updated = { ...filters, ...monthToDateRange(nextMonth) };
    setFilters(updated);
    onFilterChange?.(updated);
  };

  const handleRefresh = () => {
    onFilterChange?.({ ...filters });
  };

  const actionBtnClass =
    "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-600";

  return (
    <div className="mb-4 rounded-2xl border border-gray-100 bg-white p-3 md:p-4 shadow-sm relative">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-end">
          <div className="w-full sm:w-auto sm:shrink-0">
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
              Tháng
            </label>
            <input
              type="month"
              value={monthValue}
              onChange={(e) => handleMonthChange(e.target.value)}
              className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-700 outline-none transition-all focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-100 sm:min-w-[10.5rem]"
            />
          </div>

          <div className="min-w-0 w-full flex-1 sm:min-w-[240px] lg:max-w-xl">
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
              Tìm kiếm
            </label>
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Tìm theo tên nhân viên"
                  value={filters.keyword}
                  onChange={(e) => updateFilter("keyword", e.target.value)}
                  className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-3 text-sm font-medium text-gray-700 outline-none transition-all focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-100"
                />
              </div>
              <button
                type="button"
                onClick={handleRefresh}
                className={actionBtnClass}
                title="Làm mới dữ liệu"
              >
                <FontAwesomeIcon icon={faRotateRight} />
              </button>
              <div className="relative shrink-0" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setShowMenu((prev) => !prev)}
                  title="Tùy chọn khác"
                  className={actionBtnClass}
                >
                  <FontAwesomeIcon icon={faEllipsisH} />
                </button>

                <AnimatePresence>
                  {showMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 z-50 mt-2 w-[min(245px,calc(100vw-2rem))] overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg"
                    >
                      {hasPermission("hrm.attendance", "E") && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setExportMode("detail");
                              setExportBranchId("");
                              setExportMonth(monthInputDefault());
                              setShowExportModal(true);
                              setShowMenu(false);
                            }}
                            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-red-50"
                          >
                            <FontAwesomeIcon icon={faFileCsv} className="text-gray-500" />
                            Xuất Excel (chi tiết theo ngày)
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setExportMode("daily");
                              setExportBranchId("");
                              setExportMonth(monthInputDefault());
                              setShowExportModal(true);
                              setShowMenu(false);
                            }}
                            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-red-50"
                          >
                            <FontAwesomeIcon icon={faFileCsv} className="text-gray-500" />
                            Xuất Excel (ngày công 0.5 / 1)
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setExportMode("actual_hours");
                              setExportBranchId("");
                              setExportMonth(monthInputDefault());
                              setShowExportModal(true);
                              setShowMenu(false);
                            }}
                            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-red-50"
                          >
                            <FontAwesomeIcon icon={faFileCsv} className="text-gray-500" />
                            Xuất Excel (giờ công thực tế theo ca)
                          </button>
                        </>
                      )}

                      <button
                        type="button"
                        onClick={handleOpenManual}
                        disabled={loadingManual}
                        className={`flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-red-50 ${
                          loadingManual ? "cursor-not-allowed opacity-50" : ""
                        }`}
                      >
                        <FontAwesomeIcon icon={faBookOpen} className="text-gray-500" />
                        {loadingManual ? "Đang mở..." : "Hướng dẫn"}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 lg:pl-2">
          {attendanceStatus && attendanceStatus.current_shift && (
            <div className="hidden text-sm text-gray-500 xl:block">
              Ca {attendanceStatus.current_shift === "morning" ? "sáng" : "chiều"}
              {" "}({attendanceStatus.shift_time})
            </div>
          )}

          {hasPermission("hrm.attendance", "C") && (
            <button
              type="button"
              onClick={onNewClick}
              disabled={isDisabled}
              title={
                hasUnfinishedCheckin
                  ? "Bạn đang có ca làm chưa checkout"
                  : "Chấm công"
              }
              className={`hidden h-11 items-center gap-2 rounded-xl px-4 text-sm font-bold text-white shadow-sm transition-all md:inline-flex ${
                isDisabled
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-gradient-to-r from-red-700 to-red-800 hover:shadow-md"
              }`}
            >
              <FontAwesomeIcon icon={faFingerprint} />
              Chấm công
            </button>
          )}
        </div>
      </div>

      {hasPermission("hrm.attendance", "C") && (
        <button
          type="button"
          onClick={onNewClick}
          disabled={isDisabled}
          title={
            hasUnfinishedCheckin
              ? "Bạn đang có ca làm chưa checkout"
              : "Chấm công"
          }
          className={`fixed bottom-20 right-5 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full text-xl text-white shadow-lg transition-all active:scale-95 md:hidden ${
            isDisabled
              ? "cursor-not-allowed bg-gray-400"
              : "bg-gradient-to-r from-red-700 to-red-800 hover:scale-105"
          }`}
        >
          <FontAwesomeIcon icon={faFingerprint} />
        </button>
      )}

      <AnimatePresence>
        {showExportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4"
            onClick={() => setShowExportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-gray-800 mb-1">
                {exportMode === "detail"
                  ? "Xuất Excel — chi tiết phiếu theo ngày"
                  : exportMode === "actual_hours"
                    ? "Xuất Excel — giờ công thực tế theo ca chi nhánh"
                    : "Xuất Excel — ngày công (0.5 / 1)"}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {exportMode === "detail"
                  ? "Mỗi cột là một ngày trong tháng. Chọn chi nhánh và tháng cần kết xuất."
                  : exportMode === "actual_hours"
                    ? "Giờ công tính theo phần giao giữa giờ check-in/out và khung giờ làm việc của chi nhánh (ca sáng + chiều). Ví dụ 08:00-11:45 trong khung 07:30-11:30 => 3.5 giờ."
                    : "Mỗi ô: ≥8 giờ = 1; trên 4 giờ và dưới 8 giờ = 0.5; không quá 4 giờ = ghi “không đủ giờ công”. Cột cuối là tổng (1 + 0.5)."}
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chi nhánh</label>
                  <select
                    value={exportBranchId}
                    onChange={(e) => setExportBranchId(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-400 outline-none"
                  >
                    <option value="">Tất cả chi nhánh</option>
                    {branchOptions.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tháng / năm</label>
                  <input
                    type="month"
                    value={exportMonth}
                    onChange={(e) => setExportMonth(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-400 outline-none"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowExportModal(false)}
                  className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!exportMonth) return;
                    const branchLabel =
                      exportBranchId !== "" && exportBranchId != null
                        ? branchOptions.find((b) => String(b.value) === String(exportBranchId))
                            ?.label || "Chi nhánh"
                        : "Tất cả chi nhánh";
                    onExportXlsxRequest?.({
                      mode: exportMode,
                      branch_id: exportBranchId || undefined,
                      month: exportMonth,
                      branch_label: branchLabel,
                    });
                    setShowExportModal(false);
                  }}
                  className="flex-1 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Tải file .xlsx
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default AttendanceLogFilter;
